/**
 * @file <bk-variant-picker>: option buttons, swatches or selects (snippets/variant-picker.liquid).
 *
 * Shopify's pattern (Horizon, Dawn): on every change the chosen option value ids are sent to the
 * server as `option_values`, and the owning section is re-rendered through the Section Rendering
 * API (`?section_id=`, or `?view=` for the quick view template). Every `[data-variant-region]` of
 * the product root is then replaced by its server-rendered counterpart: this picker (availability
 * and selected states), price, chips, buy buttons, SKU, WhatsApp link and the mobile order bar.
 * So no price, availability or label is ever computed in JavaScript.
 *
 * Afterwards the `id` inputs of the product forms get the new variant (with a `change` event, which
 * Shopify's payment terms and accelerated checkout buttons listen to), `?variant=` is written into
 * the address bar on the product page, focus returns to the option the visitor used, and
 * `bk:variant:change` { variant, sectionId } is published (gallery, pickup availability).
 * A value that belongs to another product of a combined listing loads that product's page.
 */
import { publish } from '@theme/utils';

class BkVariantPicker extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {AbortController | null} */
  #request = null;

  connectedCallback() {
    this.#controller = new AbortController();
    this.addEventListener('change', (event) => this.#handleChange(event), { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#request?.abort();
    this.#request = null;
  }

  /** @returns {HTMLElement} The section's product root (every region to update lives inside it) */
  get #root() {
    const root = this.closest('[data-product-root]');
    return root instanceof HTMLElement ? root : this;
  }

  /** @returns {any} The selected variant rendered by the server, or null */
  get variant() {
    const script = this.querySelector('script[data-variant-data]');
    if (!script?.textContent) return null;
    try {
      return JSON.parse(script.textContent);
    } catch {
      return null;
    }
  }

  /** @param {Event} event */
  #handleChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLSelectElement)) return;
    if (!target.hasAttribute('data-option-position')) return;

    const chosen = target instanceof HTMLSelectElement ? target.selectedOptions[0] : target;
    if (!chosen) return;

    const connectedUrl = chosen.getAttribute('data-connected-product-url');
    if (connectedUrl && !this.#isSameProduct(connectedUrl)) {
      // Combined listing: this value is another product with its own page.
      window.location.assign(connectedUrl);
      return;
    }

    this.#paintLegend(target);
    this.#render(target.id);
  }

  /**
   * @param {string} url
   * @returns {boolean}
   */
  #isSameProduct(url) {
    const current = new URL(this.dataset.productUrl || window.location.pathname, window.location.origin);
    const next = new URL(url, window.location.origin);
    return current.pathname === next.pathname;
  }

  /**
   * Show the chosen value in the option legend right away; the server copy replaces it shortly.
   * @param {HTMLInputElement | HTMLSelectElement} target
   */
  #paintLegend(target) {
    if (!(target instanceof HTMLInputElement)) return;
    const label = target.closest('[data-variant-option]')?.querySelector('[data-option-selected]');
    if (label) label.textContent = target.value;
  }

  /** @returns {string[]} Option value ids of the current selection, in option order */
  #selectedValueIds() {
    /** @type {string[]} */
    const ids = [];
    for (const group of this.querySelectorAll('[data-variant-option]')) {
      const select = group.querySelector('select');
      const chosen = select ? select.selectedOptions[0] : group.querySelector('input[type="radio"]:checked');
      const id = chosen?.getAttribute('data-option-value-id');
      if (id) ids.push(id);
    }
    return ids;
  }

  /** @returns {URL} */
  #requestUrl() {
    const url = new URL(this.dataset.productUrl || window.location.pathname, window.location.origin);
    url.search = '';
    const ids = this.#selectedValueIds();
    if (ids.length) url.searchParams.set('option_values', ids.join(','));
    const view = this.dataset.renderView;
    if (view) url.searchParams.set('view', view);
    else if (this.dataset.sectionId) url.searchParams.set('section_id', this.dataset.sectionId);
    return url;
  }

  /** @param {string} focusId - Id of the control the visitor used */
  async #render(focusId) {
    this.#request?.abort();
    this.#request = new AbortController();
    const root = this.#root;
    root.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(this.#requestUrl(), { signal: this.#request.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const keepFocus = this.contains(document.activeElement);

      this.#swapRegions(root, html);
      const variant = this.variant;
      this.#updateVariantInputs(root, variant);
      this.#updateUrl(variant);

      if (keepFocus && focusId) {
        const control = document.getElementById(focusId);
        if (control instanceof HTMLElement) control.focus({ preventScroll: true });
      }

      publish('bk:variant:change', { variant, sectionId: this.dataset.sectionId });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      // The section could not be rendered: fall back to the product page for this selection.
      const url = new URL(this.dataset.productUrl || window.location.pathname, window.location.origin);
      const ids = this.#selectedValueIds();
      if (ids.length) url.searchParams.set('option_values', ids.join(','));
      window.location.assign(url);
    } finally {
      root.removeAttribute('aria-busy');
    }
  }

  /**
   * @param {HTMLElement} root
   * @param {Document} html
   */
  #swapRegions(root, html) {
    for (const target of root.querySelectorAll('[data-variant-region]')) {
      const key = target.getAttribute('data-variant-region');
      if (!key) continue;
      const source = html.querySelector(`[data-variant-region="${CSS.escape(key)}"]`);
      if (!source) continue;
      // An order-form app injects its own controls into these regions, next to the add button.
      // The server HTML never has them, so keep every child element the fresh HTML has no
      // counterpart for and put it back after the swap, listeners intact. Removing this is how a
      // variant change once deleted the app's order button — see lesson #1. It is not specific to
      // any one app and must survive the store changing form apps.
      const signature = (el) => `${el.tagName}#${el.id}.${el.classList[0] || ''}`;
      const theirs = new Set([...source.children].map(signature));
      const foreign = [...target.children].filter((el) => !theirs.has(signature(el)));
      target.replaceChildren(...[...source.childNodes].map((node) => document.importNode(node, true)));
      target.append(...foreign);
      this.#runScripts(target);
    }
  }

  /**
   * Scripts parsed by DOMParser never run; module scripts are re-created so the browser loads them
   * once (a module URL already loaded is not executed again).
   * @param {HTMLElement} container
   */
  #runScripts(container) {
    for (const old of container.querySelectorAll('script[src]')) {
      const script = document.createElement('script');
      script.type = old.getAttribute('type') || 'module';
      script.src = old.getAttribute('src') || '';
      old.replaceWith(script);
    }
  }

  /**
   * @param {HTMLElement} root
   * @param {any} variant
   */
  #updateVariantInputs(root, variant) {
    const id = variant?.id ? String(variant.id) : '';
    for (const input of root.querySelectorAll('input[data-variant-id-input]')) {
      if (!(input instanceof HTMLInputElement) || input.value === id) continue;
      input.value = id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /** @param {any} variant */
  #updateUrl(variant) {
    if (!this.hasAttribute('data-update-url') || !variant?.id) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variant.id));
    url.searchParams.delete('option_values');
    window.history.replaceState(window.history.state, '', url);
  }
}

if (!customElements.get('bk-variant-picker')) customElements.define('bk-variant-picker', BkVariantPicker);
